export const CLIMATE_ACTION_PATH = '/climate-action';
export const CLIMATE_ACTION_RECOMMENDATIONS_FOCUS = 'recommendations';

const SUPPORTED_SCOPES = new Set(['scope1', 'scope2', 'scope3']);

const normalizeOptionalIdentifier = (value) => {
  const normalized = value === null || value === undefined ? '' : String(value).trim();
  return normalized || null;
};

const normalizeContextMatchValue = (value) => {
  const normalized = normalizeOptionalIdentifier(value);
  return normalized
    ? normalized.normalize('NFKC').replace(/\s+/gu, ' ').toLocaleLowerCase()
    : null;
};

const normalizedBoundaryValues = (recommendation = {}) => {
  const aliases = recommendation.boundaryAliases ?? recommendation.boundary_aliases;
  return [
    recommendation.boundaryId,
    recommendation.boundary_id,
    recommendation.locationCode,
    recommendation.location_code,
    recommendation.boundaryName,
    recommendation.boundary_name,
    recommendation.location,
    ...(Array.isArray(aliases) ? aliases : []),
  ].map(normalizeContextMatchValue).filter(Boolean);
};

const normalizeFiscalYear = (value) => {
  const fiscalYear = Number(value);
  return Number.isInteger(fiscalYear) && fiscalYear >= 2000 && fiscalYear <= 2100
    ? fiscalYear
    : null;
};

/** Build the stable cross-screen URL for a scoped Climate Action recommendation context. */
export function buildClimateActionRecommendationsUrl({
  year, scope, category = null, boundaryId = null, boundaryName = null,
}) {
  const fiscalYear = normalizeFiscalYear(year);
  if (fiscalYear === null) throw new TypeError('A valid fiscal year is required.');
  if (!SUPPORTED_SCOPES.has(scope)) throw new TypeError('A supported emissions scope is required.');

  const query = new URLSearchParams();
  query.set('year', String(fiscalYear));
  query.set('scope', scope);

  const normalizedCategory = normalizeOptionalIdentifier(category);
  const normalizedBoundaryId = normalizeOptionalIdentifier(boundaryId);
  const normalizedBoundaryName = normalizeOptionalIdentifier(boundaryName);
  if (normalizedCategory) query.set('category', normalizedCategory);
  if (normalizedBoundaryId) query.set('boundaryId', normalizedBoundaryId);
  if (normalizedBoundaryName) query.set('boundaryName', normalizedBoundaryName);
  query.set('focus', CLIMATE_ACTION_RECOMMENDATIONS_FOCUS);

  return `${CLIMATE_ACTION_PATH}?${query.toString()}`;
}

/** Parse a Climate Action recommendation context from React Router location.search. */
export function parseClimateActionRecommendationsContext(search = '') {
  const query = new URLSearchParams(String(search).replace(/^\?/, ''));
  const fiscalYear = normalizeFiscalYear(query.get('year'));
  const scope = query.get('scope');
  const focus = query.get('focus');

  if (
    fiscalYear === null
    || !SUPPORTED_SCOPES.has(scope)
    || focus !== CLIMATE_ACTION_RECOMMENDATIONS_FOCUS
  ) return null;

  return {
    year: fiscalYear,
    scope,
    category: normalizeOptionalIdentifier(query.get('category')),
    boundaryId: normalizeOptionalIdentifier(query.get('boundaryId')),
    boundaryName: normalizeOptionalIdentifier(query.get('boundaryName')),
    focus: CLIMATE_ACTION_RECOMMENDATIONS_FOCUS,
  };
}

/**
 * Match a recommendation to cross-screen context.
 *
 * A boundary identifies the clicked hotspot, so it takes precedence over the
 * optional category hint. This keeps a valid office match when two screens use
 * different category aliases (for example `electricity` and
 * `purchased_electricity`). Exact boundary aliases support migrations between
 * a legacy recommendation id and the current Office Master business key without
 * using substring/fuzzy matching that could select another office.
 */
export function recommendationMatchesNavigationContext(recommendation = {}, context = null) {
  if (!context) return true;

  const expectedBoundaryId = normalizeContextMatchValue(context.boundaryId);
  const expectedBoundaryName = normalizeContextMatchValue(context.boundaryName);
  if (expectedBoundaryId || expectedBoundaryName) {
    const recommendationBoundaries = new Set(normalizedBoundaryValues(recommendation));
    return Boolean(
      (expectedBoundaryId && recommendationBoundaries.has(expectedBoundaryId))
      || (expectedBoundaryName && recommendationBoundaries.has(expectedBoundaryName)),
    );
  }

  const expectedCategory = normalizeContextMatchValue(context.category);
  if (!expectedCategory) return true;
  const recommendationCategory = normalizeContextMatchValue(
    recommendation.category
      ?? recommendation.subcategory
      ?? recommendation.catalog?.category
      ?? recommendation.catalog?.scope,
  );
  return recommendationCategory === expectedCategory;
}
